import { produce, castDraft, immerable, type Draft } from "immer";
import { FilterService } from "../../../common/domain/service";
import { GroupInfoModel, LensInGroupModel, NullGroup } from "../../model";
import { BaseModel } from "../../../core/models";
import { type GetGroupDetailsQuery } from "../../api/queries/getGroupV2.gql-generated";
import { LensListItemModel } from "../../../lenses/models";
import { DateConvert } from "../../../lenses/domain/services";
import { GroupConnection, RangeModifier } from "../../../connections/models";
import { GroupStatus, type GroupLensConnection, type LensCompoundId } from "../../../../gql-generated/types";
import { type LensUpdateData } from "./LensUpdateData";
import { type GroupDetailsCommon } from "./GroupDetailsCommon";

export class GroupDetailsModelV2 implements GroupDetailsCommon {
    public [immerable] = true;
    public readonly filteredLenses: readonly LensInGroupModel[] = [];
    public readonly info: GroupInfoModel = BaseModel.createInstance(GroupInfoModel);
    public readonly lensesUpdated: readonly LensInGroupModel[] = [];
    public readonly filterByLensName = new FilterService();

    public static createFromGroupWithConnections(groupDetails: GetGroupDetailsQuery["getGroupV2"]) {
        const { group, connections } = groupDetails ?? {};
        return BaseModel.createInstance(GroupDetailsModelV2, {
            info: BaseModel.createInstance(GroupInfoModel, {
                id: group?.id!,
                name: group?.name!,
                description: group?.description!,
                // @ts-ignore
                updatedAt: DateConvert.toDate(group?.updatedAt!),
                isArchived: group?.status === GroupStatus.Archived,
                readOnly: group?.readOnly ?? false,
            }),
            lensesUpdated: connections?.map((connection) =>
                LensInGroupModel.createFromConnection(connection as GroupLensConnection),
            ),
        }).filterAndSortLenses();
    }

    public filterByName(name: string) {
        return produce(this, (draft: Draft<GroupDetailsModelV2>) => {
            draft.filterByLensName = new FilterService(name) as Draft<FilterService>;
        }).filterAndSortLenses();
    }

    public addLenses(lenses: LensListItemModel[]) {
        const maxPriority = this.getMaxPriority();

        return produce(this, (draft: Draft<GroupDetailsModelV2>) => {
            lenses.forEach((newLens, index) => {
                // TODO update to filter on id AND reference id
                const alreadySelectedLens = draft.lensesUpdated.find((lens) => lens.id === newLens.compoundId.id);

                if (alreadySelectedLens) {
                    alreadySelectedLens.isNew = true;
                    alreadySelectedLens.isRemoved = false;
                    alreadySelectedLens.isValid = true;
                    alreadySelectedLens.scheduleRange = new RangeModifier();
                    alreadySelectedLens.priority = maxPriority + index + 1;
                } else {
                    draft.lensesUpdated.push(
                        castDraft(LensInGroupModel.createFromLens(newLens, maxPriority + index + 1)),
                    );
                }
            });
        }).filterAndSortLenses();
    }

    public updateLens({ priority, isValid, scheduleRange, id: lensId }: LensUpdateData) {
        return produce(this, (draft: Draft<GroupDetailsModelV2>) => {
            draft.lensesUpdated = draft.lensesUpdated.map((lensInGroup) =>
                lensInGroup.id === lensId
                    ? produce(lensInGroup, (lensInGroupDraft: Draft<LensInGroupModel>) => {
                          const draftLensInGroup = castDraft(lensInGroupDraft);
                          draftLensInGroup.priority = priority;
                          draftLensInGroup.isValid = isValid;
                          draftLensInGroup.scheduleRange = scheduleRange;
                          draftLensInGroup.isUpdated = true;
                      })
                    : lensInGroup,
            );
        }).filterAndSortLenses();
    }

    public removeLens(lensId: string) {
        return produce(this, (draft: Draft<GroupDetailsModelV2>) => {
            const lensToRemove = draft.lensesUpdated.find(({ id }) => id === lensId);
            if (lensToRemove) {
                if (lensToRemove.isNew) {
                    draft.lensesUpdated = draft.lensesUpdated.filter((lens) => lens.id !== lensId);
                } else {
                    lensToRemove.isRemoved = true;
                }
            }
        }).filterAndSortLenses();
    }

    public updateName(newName: string) {
        return produce(this, (draft: Draft<GroupDetailsModelV2>) => {
            draft.info.isChanged = true;
            draft.info.name = newName;
        });
    }

    public updateDescription(newDescription: string) {
        return produce(this, (draft: Draft<GroupDetailsModelV2>) => {
            draft.info.isChanged = true;
            draft.info.description = newDescription;
        });
    }

    public get nameFilterValue() {
        return this.filterByLensName.maybeFilter;
    }

    public get isValid() {
        return this.lensesUpdated.every((lens) => lens.isRemoved || lens.isValid);
    }

    public get isChanged() {
        return this.lensesUpdated.some((lens) => lens.isUpdated || lens.isRemoved || lens.isNew) || this.info.isChanged;
    }

    public getLensesIds() {
        return this.lensesUpdated.filter((lens) => !lens.isRemoved).map(({ id }) => id) as string[];
    }

    public get isNull() {
        return this.info === NullGroup;
    }

    public get newOrUpdatedConnections(): readonly GroupConnection[] {
        return this.lensesUpdated
            .filter((lens) => !lens.isRemoved && (lens.isNew || lens.isUpdated))
            .map((lens) => lens.toGroupConnection());
    }

    public get removedLensIds(): readonly LensCompoundId[] {
        return this.lensesUpdated
            .filter((lens) => lens.isRemoved)
            .map((lens) => lens.lens.compoundId) as readonly LensCompoundId[];
    }

    private filterAndSortLenses() {
        return produce(this, (draft: Draft<GroupDetailsModelV2>) => {
            draft.filteredLenses = castDraft(
                [
                    ...this.filterByLensName.applyFilter(
                        this.lensesUpdated.filter((lens) => !lens.isRemoved),
                        (lens) => lens!.lens!.name as string,
                    ),
                ].sort((lensA, lensB) => lensB.priority - lensA.priority),
            );
        });
    }

    private getMaxPriority(): number {
        return this.lensesUpdated
            .filter((lens) => !lens.isRemoved)
            .reduce((prevPriority, nextLens) => Math.max(prevPriority, nextLens.priority), 0);
    }
}
